import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, Shield, FileText, Bell, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';

const SettingsPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState(null);
  const [selectedItem, setSelectedItem] = useState('account-preferences');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadProfilePicture();
  }, [isAuthenticated, navigate]);

  const loadProfilePicture = async () => {
    try {
      const resumes = await resumeAPI.fetchAll();
      const primaryResume = resumes.find(r => r.is_primary) || resumes[0];
      if (primaryResume?.photo_url) {
        setProfilePicture(primaryResume.photo_url);
      }
    } catch (error) {
      console.error('Error loading profile picture:', error);
    }
  };

  const menuItems = [
    { id: 'account-preferences', label: 'Account preferences', icon: User },
    { id: 'sign-in-security', label: 'Sign in & security', icon: Lock },
    { id: 'visibility', label: 'Visibility', icon: Eye },
    { id: 'data-privacy', label: 'Data privacy', icon: Shield },
    { id: 'advertising-data', label: 'Advertising data', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  const SettingsCard = ({ title, children }) => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="divide-y divide-gray-200">{children}</div>
    </div>
  );

  const SettingsRow = ({ title, subtitle, rightText, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full px-6 py-4 hover:bg-gray-50 transition-colors text-left flex items-center justify-between gap-6"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {subtitle ? <p className="text-xs text-gray-600 mt-0.5">{subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {rightText ? <span className="text-xs text-gray-600">{rightText}</span> : null}
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Left sidebar */}
          <aside className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 h-fit">
            <div className="flex items-center gap-3 mb-6">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center border border-gray-200">
                  <User className="w-6 h-6 text-primary-600" />
                </div>
              )}
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            </div>

            <nav className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = selectedItem === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (item.id === 'sign-in-security') {
                        navigate('/settings/security');
                      } else {
                        setSelectedItem(item.id);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                      isActive ? 'text-green-700 bg-green-50' : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-green-700' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${isActive ? 'text-green-700' : 'text-gray-800'}`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Right content */}
          <section className="space-y-6">
            {selectedItem === 'account-preferences' ? (
              <>
                <SettingsCard title="Profile information">
                  <SettingsRow
                    title="Name, location, and industry"
                    onClick={() => {}}
                  />
                  <SettingsRow
                    title="Personal demographic information"
                    onClick={() => {}}
                  />
                  <SettingsRow
                    title="Verifications"
                    onClick={() => {}}
                  />
                </SettingsCard>

                <SettingsCard title="Display">
                  <SettingsRow title="Dark mode" onClick={() => {}} />
                </SettingsCard>

                <SettingsCard title="General preferences">
                  <SettingsRow title="Language" onClick={() => {}} />
                  <SettingsRow title="Content language" onClick={() => {}} />
                  <SettingsRow title="Autoplay videos" rightText="On" onClick={() => {}} />
                  <SettingsRow title="Sound effects" rightText="On" onClick={() => {}} />
                  <SettingsRow title="Showing profile photos" rightText="All CBN members" onClick={() => {}} />
                  <SettingsRow title="Feed preferences" onClick={() => {}} />
                  <SettingsRow title="Preferred feed view" rightText="Most relevant posts (Recommended)" onClick={() => {}} />
                  <SettingsRow title="People you unfollowed" onClick={() => {}} />
                </SettingsCard>
              </>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {menuItems.find(m => m.id === selectedItem)?.label}
                </h2>
                <p className="text-sm text-gray-600">
                  This section will be built next.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
